import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  campaignsListCampaignJoinInvitesOptions,
  campaignsListCampaignJoinInvitesQueryKey,
} from '@givmo/api-client/query';
import { extractList, isGivmoError, sdk, type P2pJoinInviteOut } from '@givmo/api-client';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  TextField,
  toast,
} from '@givmo/design-system';
import { ConfirmDialog } from './confirm-dialog';
import { displayP2pDate, teamStatusLabel } from './p2p-decode';
import { useLivePage } from './use-live-page';

const PAGE_SIZE = 25;

/** OWNER-only child. Tokens and token hashes are never present in this DTO. */
export function CampaignInvites({ campaignId }: { campaignId: string }) {
  const [email, setEmail] = useState('');
  const [offset, setOffset] = useState(0);
  const [revoke, setRevoke] = useState<P2pJoinInviteOut | null>(null);
  const live = useLivePage();
  const client = useQueryClient();
  const options = campaignsListCampaignJoinInvitesOptions({
    path: { fundraiser_id: campaignId },
    query: { 'page[limit]': PAGE_SIZE + 1, 'page[offset]': offset },
  });
  const query = useQuery({
    ...options,
    select: (body: unknown) => {
      const rows = extractList<P2pJoinInviteOut>(body);
      if (rows.some((row) => row.campaign_id !== campaignId || typeof row.email !== 'string'))
        throw new Error('The invitations could not be read.');
      return rows;
    },
  });
  const rows = query.data?.slice(0, PAGE_SIZE) ?? [];
  const refresh = () =>
    client.invalidateQueries({
      queryKey: campaignsListCampaignJoinInvitesQueryKey({ path: { fundraiser_id: campaignId } }),
    });
  function failed(error: unknown) {
    if (live())
      toast.error(
        isGivmoError(error) ? error.message : 'Couldn’t update invitations. Please try again.',
      );
  }
  const create = useMutation({
    mutationFn: () =>
      sdk.campaignsCreateCampaignJoinInvite({
        path: { fundraiser_id: campaignId },
        body: { email: email.trim() },
        throwOnError: true,
      }),
    onSuccess: async () => {
      await refresh();
      if (live()) {
        setEmail('');
        setOffset(0);
        toast.success('Invitation created');
      }
    },
    onError: failed,
  });
  const withdraw = useMutation({
    mutationFn: (invite: P2pJoinInviteOut) =>
      sdk.campaignsRevokeCampaignJoinInvite({
        path: { fundraiser_id: campaignId, invite_id: invite.id },
        throwOnError: true,
      }),
    onSuccess: async () => {
      await refresh();
      if (live()) {
        setRevoke(null);
        toast.success('Invitation revoked');
      }
    },
    onError: failed,
  });
  return (
    <Card flush testId="p2p-invites">
      <CardHeader
        title="Invitations"
        subtitle="Invite a supporter by email and manage pending invitations."
      />
      <CardBody className="flex flex-col gap-16">
        <form
          className="flex flex-col gap-12 sm:flex-row sm:items-end"
          onSubmit={(event) => {
            event.preventDefault();
            if (query.isSuccess && email.trim() && !create.isPending) create.mutate();
          }}
        >
          <TextField
            type="email"
            required
            label="Email address"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={create.isPending || !query.isSuccess}
            className="flex-1"
            testId="p2p-invite-email"
          />
          <Button
            type="submit"
            appearance="saas"
            tone="secondary"
            disabled={!query.isSuccess || !email.trim() || create.isPending}
            loading={create.isPending}
            testId="p2p-invite-create"
          >
            Invite supporter
          </Button>
        </form>
        {query.isPending ? (
          <p role="status" className="text-s13 text-text-secondary">
            Loading invitations…
          </p>
        ) : query.isError ? (
          <EmptyState
            testId="p2p-invites-error"
            title="Couldn’t load invitations"
            action={
              <Button
                appearance="saas"
                tone="secondary"
                testId="p2p-invites-retry"
                onClick={() => void query.refetch()}
              >
                Try again
              </Button>
            }
          />
        ) : rows.length === 0 ? (
          <p className="text-s13 text-text-secondary" data-testid="p2p-invites-empty">
            No invitations yet.
          </p>
        ) : (
          <ul className="divide-y divide-border-subtle">
            {rows.map((invite) => (
              <li
                key={invite.id}
                className="flex flex-wrap items-center gap-12 py-12 first:pt-0 last:pb-0"
                data-testid={`p2p-invite-${invite.id}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="break-all text-s14 text-text-strong">{invite.email}</p>
                  <p className="mt-4 text-s12 text-text-secondary">
                    Expires {displayP2pDate(invite.expires_at)}
                  </p>
                </div>
                <Badge tone="neutral">{teamStatusLabel(invite.status)}</Badge>
                {invite.status === 'pending' && (
                  <Button
                    appearance="saas"
                    tone="ghost"
                    disabled={withdraw.isPending}
                    testId={`p2p-invite-revoke-${invite.id}`}
                    onClick={() => setRevoke(invite)}
                  >
                    Revoke
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
        {(offset > 0 || (query.data?.length ?? 0) > PAGE_SIZE) && (
          <div className="flex justify-between gap-12">
            <Button
              appearance="saas"
              tone="secondary"
              testId="p2p-invites-previous"
              disabled={offset === 0 || query.isPending}
              onClick={() => setOffset((current) => Math.max(0, current - PAGE_SIZE))}
            >
              Previous
            </Button>
            <Button
              appearance="saas"
              tone="secondary"
              testId="p2p-invites-next"
              disabled={(query.data?.length ?? 0) <= PAGE_SIZE || query.isPending}
              onClick={() => setOffset((current) => current + PAGE_SIZE)}
            >
              Next
            </Button>
          </div>
        )}
        <ConfirmDialog
          open={revoke !== null}
          onOpenChange={(open) => {
            if (!open && !withdraw.isPending) setRevoke(null);
          }}
          title="Revoke this invitation?"
          body="The invitation link will no longer let this supporter join the campaign."
          confirmLabel="Revoke invitation"
          tone="danger"
          testId="p2p-invite-revoke-dialog"
          cancelTestId="p2p-invite-revoke-cancel"
          confirmTestId="p2p-invite-revoke-confirm"
          busy={withdraw.isPending}
          onConfirm={() => {
            if (revoke && !withdraw.isPending) withdraw.mutate(revoke);
          }}
        />
      </CardBody>
    </Card>
  );
}
